import type { IAffiliateShowCurrentTrackerResponse } from '@modules/affiliates-approved/submodules/trackers-settings/types';
import type { IContextMutationResult } from '@modules/core/services/query/types';
import {
  AFFILIATE_CURRENT_TRACKER_QUERY,
  AFFILIATES_APPROVED_TRACKERS_LIST_PERMISSIONS,
} from '@modules/affiliates-approved/submodules/trackers-settings/submodules/trackers-list/constants';
import { useRestClient } from '@modules/core/services/api';
import { useContextMutation } from '@modules/core/services/query';
import { useToastStore } from '@modules/toast/store';
import { isString } from '@utils/guards';

import type { ITrackerHistoryChangeItemResponse, ITrackerHistoryChangeItemUpdatePayload, ITrackerHistoryChangeItemUpdateResponse } from '../types';
import {
  TRACKER_HISTORY_CHANGES_ITEM_QUERY,
  TRACKER_HISTORY_CHANGES_ITEM_UPDATE_API_URL,
  TRACKER_HISTORY_CHANGES_ITEM_UPDATE_MUTATION,
} from '../constants';

export default function useTrackerHistoryChangesItemUpdateMutation(): IContextMutationResult<
  ITrackerHistoryChangeItemUpdateResponse,
  Error,
  ITrackerHistoryChangeItemUpdatePayload
> {
  const { fetchPut } = useRestClient();
  const { displayPresetToast } = useToastStore();

  return useContextMutation<ITrackerHistoryChangeItemUpdateResponse, Error, ITrackerHistoryChangeItemUpdatePayload>({
    mutationKey: [TRACKER_HISTORY_CHANGES_ITEM_UPDATE_MUTATION],
    mutationFn: ({ itemId, ...payload }) => fetchPut<ITrackerHistoryChangeItemUpdateResponse>(
      TRACKER_HISTORY_CHANGES_ITEM_UPDATE_API_URL,
      payload,
      {
        permission: AFFILIATES_APPROVED_TRACKERS_LIST_PERMISSIONS.HISTORY_CHANGES_EDIT,
        urlParams: {
          itemId,
        },
        queryParams: { merchant: payload.merchant },
      },
    ),
    async onSuccess({ queryClient, data, variables }) {
      if (!data?.history_of_trackers_changes) {
        await queryClient.invalidateQueries({ queryKey: [TRACKER_HISTORY_CHANGES_ITEM_QUERY, variables.itemId] });
        await queryClient.invalidateQueries({ queryKey: [AFFILIATE_CURRENT_TRACKER_QUERY] });
        return;
      }

      const historyItem = data.history_of_trackers_changes.find((val) => val.id === variables.itemId);
      const newDate = isString(historyItem?.message.data.date) ? historyItem?.message.data.date : undefined;

      queryClient.setQueriesData<ITrackerHistoryChangeItemResponse>(
        { queryKey: [TRACKER_HISTORY_CHANGES_ITEM_QUERY, variables.itemId] },
        (queryData) => {
          if (!queryData) {
            return queryData;
          }

          return {
            ...queryData,
            history_of_trackers_change: {
              ...queryData.history_of_trackers_change,
              condition_id: variables.history_of_trackers_change.condition_id,
              date: newDate || queryData.history_of_trackers_change.date,
            },
          };
        },
      );

      queryClient.setQueriesData<IAffiliateShowCurrentTrackerResponse>(
        { queryKey: [AFFILIATE_CURRENT_TRACKER_QUERY] },
        (queryData) => {
          if (!queryData) {
            return queryData;
          }

          return {
            ...queryData,
            tracker: {
              ...queryData.tracker,
              history_of_trackers_changes: [...data.history_of_trackers_changes],
            },
          };
        },
      );

      displayPresetToast('edited');
    },
  });
}
